import React, { useState } from "react";

import { Accordion, AccordionSummary, AccordionDetails, CircularProgress } from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

export default function DashboardLazyAccordion({
  title,
  subtitle,
  buttonLabel,
  loading = false,
  loaded = false,
  empty = false,
  emptyMessage = "Não foram encontrados dados para apresentar.",
  onRefresh,
  children,
  headerContent = null,
  initialExpanded = false,
  showRefreshButton = true,
}) {
  const [expanded, setExpanded] = useState(initialExpanded);

  const handleRefresh = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (loading || typeof onRefresh !== "function") return;

    setExpanded(true);
    await onRefresh();
  };

  return (
    <MDBox mb={2}>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        disableGutters
        sx={{
          border: "1px solid rgba(0,0,0,0.04)",
          borderRadius: "12px !important",
          boxShadow: "0 8px 20px rgba(0,0,0,0.16)",
          overflow: "hidden",
          backgroundColor: "#fff",
          transition:
            "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",

          "&::before": {
            display: "none",
          },

          "&.Mui-expanded": {
            margin: 0,
            backgroundColor: "#fff",
            boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            position: "relative",
            pl: { xs: 2, md: 3 },
            pr: { xs: 2, md: 3 },
            py: 0.5,
            minHeight: 54,
            backgroundColor: "transparent",

            "&.Mui-expanded": {
              minHeight: 54,
            },

            "&.Mui-expanded::after": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: 4,
              backgroundColor: "error.main",
              zIndex: 1,
            },

            "& .MuiAccordionSummary-content": {
              my: 1,
              minWidth: 0,
            },

            "& .MuiAccordionSummary-content.Mui-expanded": {
              my: 1,
            },
          }}
        >
          <MDBox
            display="flex"
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            gap={1.5}
            flexWrap="wrap"
            sx={{ width: "100%", minWidth: 0, pr: 1 }}
          >
            <MDBox sx={{ minWidth: 0, flex: 1 }}>
              <MDTypography variant="h6" sx={{ whiteSpace: "normal" }}>
                {title}
              </MDTypography>

              {subtitle && (
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{
                    display: "block",
                    mt: 0.25,
                    whiteSpace: "normal",
                  }}
                >
                  {subtitle}
                </MDTypography>
              )}
            </MDBox>

            {(headerContent || showRefreshButton) && (
              <MDBox
                display="flex"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                sx={{
                  width: { xs: "100%", md: "auto" },
                }}
                onClick={(event) => event.stopPropagation()}
                onFocus={(event) => event.stopPropagation()}
              >
                {headerContent}

                {showRefreshButton && (
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    onClick={handleRefresh}
                    disabled={loading}
                    startIcon={
                      loading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <RefreshIcon fontSize="small" />
                      )
                    }
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      whiteSpace: "normal",
                    }}
                  >
                    {buttonLabel}
                  </MDButton>
                )}
              </MDBox>
            )}
          </MDBox>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            px: { xs: 1.5, md: 2.5 },
            pt: 0.5,
            pb: 2.5,
          }}
        >
          {loading && !loaded ? (
            <MDBox py={3} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </MDBox>
          ) : !loaded ? (
            <MDBox py={2}>
              <MDTypography
                variant="button"
                color="text"
                sx={{
                  display: "block",
                  textAlign: "center",
                  fontWeight: "regular",
                }}
              >
                Clique em Atualizar
              </MDTypography>
            </MDBox>
          ) : empty ? (
            <MDBox py={2}>
              <MDTypography
                variant="button"
                color="text"
                sx={{
                  display: "block",
                  textAlign: "center",
                  fontWeight: "regular",
                  whiteSpace: "normal",
                }}
              >
                {emptyMessage}
              </MDTypography>
            </MDBox>
          ) : null}

          <MDBox
            sx={{
              display: loaded && !empty ? "block" : "none",
              width: "100%",
              maxWidth: "100%",
            }}
          >
            {children}
          </MDBox>
        </AccordionDetails>
      </Accordion>
    </MDBox>
  );
}
